import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbHash, TbLock } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { parseSlackChannelsPayload, type SlackChannelsPayload } from "./slackChannelsSchema";

interface SlackChannelsRendererProps {
  block: StreamingBlock;
}

export const SlackChannelsRenderer = (props: SlackChannelsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<SlackChannelsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseSlackChannelsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("slack_channels_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().channels.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbHash class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("slack_channels_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().totalResults === 1
                    ? rt.t("slack_channels_card.results_one")
                    : rt.t("slack_channels_card.results", { count: String(data().totalResults) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().channels}>
                  {(channel) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-center gap-1.5">
                          <Show
                            when={channel.isPrivate}
                            fallback={<TbHash class="size-3.5 shrink-0 text-muted-text" aria-hidden="true" />}
                          >
                            <TbLock
                              class="size-3.5 shrink-0 text-muted-text"
                              aria-label={rt.t("slack_channels_card.private")}
                            />
                          </Show>
                          <span class="truncate text-sm text-primary-text">{channel.name}</span>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{channel.id}</span>
                      </div>
                      <Show when={channel.topic}>
                        <p class="mt-0.5 truncate text-xs text-muted-text">{channel.topic}</p>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
